<template>
  <div class="profile-container">
    <el-card shadow="hover" class="profile-card">
      <template #header>
        <div class="card-header">
          <h2>个人资料</h2>
        </div>
      </template>
      
      <div class="profile-content">
        <!-- 用户基本信息 -->
        <el-card class="info-card" shadow="hover">
          <template #header>
            <div class="sub-card-header">
              <h3>基本信息</h3>
            </div>
          </template>
          
          <div class="info-section">
            <div class="info-item">
              <span class="label">头像:</span>
              <el-avatar size="large" :src="userInfo?.avatar || require('../../assets/logo.png')" />
            </div>
            <div class="info-item">
              <span class="label">用户名:</span>
              <span class="value">{{ userInfo?.name || '未知用户' }}</span>
            </div>
            <div class="info-item">
              <span class="label">角色:</span>
              <el-tag :type="userInfo?.role === 'admin' ? 'primary' : 'success'">
                {{ userInfo?.role === 'admin' ? '系统管理员' : '酒店员工' }}
              </el-tag>
            </div>
            <div class="info-item">
              <span class="label">邮箱:</span>
              <span class="value">{{ userInfo?.email || '未知邮箱' }}</span>
            </div>
          </div>
        </el-card>
        
        <!-- 修改密码 -->
        <el-card class="password-card" shadow="hover">
          <template #header>
            <div class="sub-card-header">
              <h3>修改密码</h3>
            </div>
          </template>
          
          <div class="password-section">
            <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="120px">
              <el-form-item label="当前密码" prop="oldPassword">
                <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入当前密码" show-password />
              </el-form-item>
              <el-form-item label="新密码" prop="newPassword">
                <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" show-password />
              </el-form-item>
              <el-form-item label="确认密码" prop="confirmPassword">
                <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="handleUpdatePassword" :loading="loading">确认修改</el-button>
                <el-button @click="resetForm">重置</el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, reactive, computed } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { ElMessage } from 'element-plus';
import * as authApi from '../../api/auth';

export default {
  name: 'Profile',
  setup() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const passwordFormRef = ref();
    
    // 使用computed获取响应式的userInfo
    const userInfo = computed(() => authStore.userInfo);
    
    // 密码表单
    const passwordForm = reactive({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    
    // 验证新密码复杂度
    const validateNewPassword = (rule, value, callback) => {
      // 新密码不能与当前密码相同
      if (value === passwordForm.oldPassword) {
        callback(new Error('新密码不能与当前密码相同'));
        return;
      }
      
      // 不允许纯数字
      if (/^\d+$/.test(value)) {
        callback(new Error('密码不能为纯数字'));
        return;
      }
      
      // 不允许纯字母
      if (/^[a-zA-Z]+$/.test(value)) {
        callback(new Error('密码不能为纯字母'));
        return;
      }
      
      // 检查是否包含递增或递减的连续序列
      const checkContinuousSequence = (val) => {
        const sequences = ['012', '123', '234', '345', '456', '567', '678', '789', '890', 
                          '987', '876', '765', '654', '543', '432', '321', '210',
                          'abc', 'bcd', 'cde', 'def', 'efg', 'fgh', 'ghi', 'hij', 'ijk', 
                          'jkl', 'klm', 'lmn', 'mno', 'nop', 'opq', 'pqr', 'qrs', 'rst', 
                          'stu', 'tuv', 'uvw', 'vwx', 'wxy', 'xyz',
                          'zyx', 'yxw', 'xwv', 'wvu', 'vut', 'uts', 'tsr', 'srq', 'rqp', 
                          'qpo', 'pon', 'onm', 'nml', 'mlk', 'lkj', 'jih', 'ihg', 'hgf', 
                          'gfe', 'fed', 'edc', 'dcb', 'cba'];
        return sequences.some(seq => val.toLowerCase().includes(seq));
      };
      
      // 不允许连续的递增/递减数字或字母序列
      if (checkContinuousSequence(value)) {
        callback(new Error('密码不能包含连续的数字或字母序列'));
        return;
      }
      
      // 不允许连续相同的字符超过3个
      if (/([a-zA-Z0-9])\1{3,}/.test(value)) {
        callback(new Error('密码不能包含连续4个及以上相同的字符'));
        return;
      }
      
      callback();
    };
    
    // 验证确认密码与新密码一致
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'));
      } else {
        callback();
      }
    };
    
    // 密码表单验证规则
    const passwordRules = {
      oldPassword: [
        { required: true, message: '请输入当前密码', trigger: 'blur' },
        { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
      ],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' },
        { validator: validateNewPassword, trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        { validator: validateConfirmPassword, trigger: 'blur' }
      ]
    };
    
    // 初始化用户信息
    const initUserInfo = async () => {
      try {
        await authStore.getCurrentUser();
      } catch (error) {
        ElMessage.error('获取用户信息失败');
        console.error('获取用户信息失败:', error);
      }
    };
    
    // 处理修改密码
    const handleUpdatePassword = async () => {
      if (!passwordFormRef.value) return;
      
      await passwordFormRef.value.validate(async (valid) => {
        if (valid) {
          loading.value = true;
          try {
            await authApi.updatePassword({
              old_password: passwordForm.oldPassword,
              new_password: passwordForm.newPassword,
              confirm_password: passwordForm.confirmPassword
            });
            
            ElMessage.success('密码修改成功，将自动退出登录重新登录');
            
            // 延迟1秒后退出登录
            setTimeout(async () => {
              try {
                await authStore.logout();
                // 跳转到登录页面
                window.location.href = '/login';
              } catch (error) {
                console.error('退出登录失败:', error);
                // 即使退出登录失败，也强制跳转到登录页面
                window.location.href = '/login';
              }
            }, 1000);
          } catch (error) {
            ElMessage.error(error.response?.data?.message || '密码修改失败');
            console.error('密码修改失败:', error);
          } finally {
            loading.value = false;
          }
        }
      });
    };
    
    // 重置表单
    const resetForm = () => {
      if (passwordFormRef.value) {
        passwordFormRef.value.resetFields();
      }
    };
    
    // 组件挂载时初始化
    onMounted(() => {
      initUserInfo();
    });
    
    return {
      userInfo,
      loading,
      passwordForm,
      passwordRules,
      passwordFormRef,
      handleUpdatePassword,
      resetForm
    };
  }
};
</script>

<style scoped>
.profile-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.profile-card {
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  color: #303133;
  font-size: 20px;
}

.profile-content {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.info-card,
.password-card {
  flex: 1;
  min-width: 300px;
  margin-bottom: 20px;
}

.sub-card-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.info-section {
  padding: 10px 0;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #606266;
  font-size: 14px;
  min-width: 80px;
}

.value {
  color: #303133;
  font-size: 14px;
}

.password-section {
  padding: 10px 0;
}
</style>